import React from "react";
import { ChevronDown, Zap } from "lucide-react";
import Sigil from "./Sigil";
import { Sparks, FloatingRunes } from "./Atmosphere";

const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-between overflow-hidden border-b border-[#2A0A0A]"
    >
      {/* Warp starfield */}
      <div className="starfield">
        <div className="warp-streaks" />
      </div>
      <FloatingRunes count={14} />
      <Sparks count={22} />

      {/* Top status bar */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-[#2A0A0A]/80 backdrop-blur-sm bg-black/30">
        <div className="flex items-center gap-3" data-testid="brand-mark">
          <Sigil size={36} />
          <div className="leading-tight">
            <div className="font-display text-[#E6B836] text-lg tracking-widest">qapla<span className="text-[#FF2A2A]">.uk</span></div>
            <div className="font-mono-k text-[10px] text-[#A0A0A0] uppercase tracking-[0.3em]">tlhIngan&nbsp;Wo'&nbsp;//&nbsp;imperio&nbsp;digital</div>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-8 font-mono-k text-[11px] uppercase tracking-[0.3em] text-[#A0A0A0]">
          <a data-testid="nav-link-video" href="#transmision" className="hover:text-[#E6B836] transition-colors">// transmisión</a>
          <a data-testid="nav-link-apps" href="#apps" className="hover:text-[#E6B836] transition-colors">// arsenal</a>
          <a data-testid="nav-link-lore" href="#lore" className="hover:text-[#E6B836] transition-colors">// código de honor</a>
        </nav>
        <div className="hidden md:flex items-center gap-2 text-[10px] font-mono-k uppercase tracking-[0.3em] text-[#A0A0A0]">
          <span className="h-2 w-2 rounded-full bg-[#FF2A2A] animate-pulse" />
          stardate&nbsp;78972.6
        </div>
      </header>

      {/* Hero body */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-12 max-w-7xl mx-auto w-full py-16">
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-6" data-testid="hero-eyebrow">
          <div className="bar-bronze" />
          <span className="font-mono-k text-[11px] uppercase tracking-[0.4em] text-[#C59B27]">
            transmisión&nbsp;abierta&nbsp;·&nbsp;canal&nbsp;qonoS
          </span>
        </div>

        {/* Klingon banner phrase */}
        <p className="font-rune text-[#FF6A6A] text-sm md:text-base tracking-[0.45em] mb-4 glitch-soft" data-testid="hero-klingon-banner">
          Heghlu'meH&nbsp;QaQ&nbsp;jajvam
        </p>

        {/* Massive glitched title */}
        <h1
          data-testid="hero-title"
          className="font-display font-black uppercase leading-[0.85] text-[#EAEAEA] text-6xl sm:text-7xl md:text-8xl lg:text-[10rem]"
        >
          <span className="glitch block" data-text="qapla.">qapla.</span>
          <span className="glitch block text-[#FF2A2A]" data-text="uk">uk</span>
        </h1>

        {/* Sub */}
        <p
          data-testid="hero-subtitle"
          className="mt-8 max-w-2xl font-mono-k text-[#A0A0A0] text-base md:text-lg leading-relaxed"
        >
          La frontera digital del{" "}
          <span className="text-[#E6B836]">Imperio Klingon</span>. Aquí{" "}
          <span className="text-[#EAEAEA]">Bicio</span> forja apps con honor — afiladas
          como una <em className="not-italic text-[#FF6A6A]">bat'leth</em>, brutales como una
          incursión en warp.{" "}
          <span className="font-rune text-[#C59B27]">Qapla'</span> significa éxito. Aquí lo construimos.
        </p>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="hero-cta-row">
          <a href="#apps" className="k-btn" data-testid="cta-enter-empire">
            <Zap size={14} strokeWidth={2.5} />
            <span>Acceder al arsenal</span>
            <span className="arrow">→</span>
          </a>
          <a href="#transmision" className="k-btn k-btn-ghost" data-testid="cta-watch-transmission">
            <span>Ver transmisión</span>
            <span className="arrow">→</span>
          </a>
        </div>

        {/* HUD readouts */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl" data-testid="hero-hud">
          {[
            { k: "may'", v: "12 batallas", lbl: "apps desplegadas" },
            { k: "HoS", v: "98.4%", lbl: "uptime imperial" },
            { k: "veS", v: "0 prisioneros", lbl: "código revisado" },
            { k: "batlh", v: "∞", lbl: "honor restante" },
          ].map((s, i) => (
            <div key={i} className="border border-[#2A0A0A] bg-black/40 backdrop-blur-sm p-4 panel-corner-tl panel-corner-tr relative">
              <div className="font-rune text-[#C59B27] text-xs tracking-[0.3em]">{s.k}</div>
              <div className="font-display text-[#EAEAEA] text-xl mt-1">{s.v}</div>
              <div className="font-mono-k text-[9px] uppercase tracking-[0.2em] text-[#555] mt-1">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom scroll prompt */}
      <div className="relative z-10 flex items-center justify-center pb-8" data-testid="hero-scroll-prompt">
        <div className="flex flex-col items-center gap-2 text-[#A0A0A0] font-mono-k text-[10px] uppercase tracking-[0.4em]">
          <span>desplegar</span>
          <ChevronDown size={18} className="animate-bounce text-[#C59B27]" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
